import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Filter, SortAsc, X, ChevronDown } from 'lucide-react';
import Layout from '../components/layout/Layout';
import ProductCard from '../components/products/ProductCard';
import SearchBar from '../components/common/SearchBar';
import Pagination from '../components/common/Pagination';
import * as Types from '../services/api/api.types';
import { config } from '../config/env';
import { useCart } from '../contexts/CartContext';
import { apiService } from '../services/api/instance';
import logger from '../utils/logger';
import { useScrollRestoration } from '../hooks/useScrollRestoration';

const SearchResultsPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const query = searchParams.get('query') || '';

    const [allProducts, setAllProducts] = useState<Types.ProductFull[]>([]);
    const [filteredProducts, setFilteredProducts] = useState<Types.ProductFull[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showFilters, setShowFilters] = useState(false);
    // Relevance is the only default that answers the query: sorting by price puts
    // whatever cheap accessory happens to match ahead of the product searched for.
    const [sortBy, setSortBy] = useState<'relevance' | 'price-asc' | 'price-desc' | 'name'>('relevance');
    const [selectedManufacturers, setSelectedManufacturers] = useState<Set<string>>(new Set());
    const [priceRange, setPriceRange] = useState<{min: number | null, max: number | null}>({min: null, max: null});
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const { cart } = useCart();
    const productsPerPage = 50;

    // Fetch products using the backend search API
    useEffect(() => {
        const fetchSearchResults = async () => {
            if (!query || query.trim().length < 2) {
                setAllProducts([]);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);

                // Use the new backend search endpoint - fetch more results for client-side filtering
                const searchResults = await apiService.searchProducts(query, 1, 200);
                setAllProducts(searchResults);

            } catch (error) {
                logger.error('Грешка при търсенето:', error);
                setAllProducts([]);
            } finally {
                setLoading(false);
            }
        };

        fetchSearchResults();
    }, [query]); // Re-fetch when query changes

    // Get unique manufacturers from all products
    const manufacturers = useMemo(() => {
        const uniqueManufacturers = new Set<string>();
        allProducts.forEach(product => {
            if (product.manufacturer) {
                uniqueManufacturers.add(product.manufacturer);
            }
        });
        return Array.from(uniqueManufacturers).sort();
    }, [allProducts]);

    // Apply filters and sorting
    const processedProducts = useMemo(() => {
        if (allProducts.length === 0) return [];

        let filtered = [...allProducts];

        // Apply manufacturer filter
        if (selectedManufacturers.size > 0) {
            filtered = filtered.filter(product =>
                product.manufacturer && selectedManufacturers.has(product.manufacturer)
            );
        }

        // Apply price filter
        if (priceRange.min !== null || priceRange.max !== null) {
            filtered = filtered.filter(product => {
                const price = product.price_promo || product.price_partner;
                if (priceRange.min !== null && price < priceRange.min) return false;
                if (priceRange.max !== null && price > priceRange.max) return false;
                return true;
            });
        }

        // Apply sorting
        let sorted: Types.ProductFull[] = [];

        // Relevance is the order the backend already ranked the results in. It
        // scores what a fuzzy match over one fetched page cannot see - stock,
        // whole part numbers, and whether a product is the thing searched for or
        // just contains one - so re-ranking here only discarded that work.
        if (sortBy === 'relevance' && query) {
            const inStockSorted = filtered.filter(p => p.status === 1);
            const outOfStockSorted = filtered.filter(p => p.status !== 1);
            sorted = [...inStockSorted, ...outOfStockSorted];
        } else {
            // Original sorting logic for non-relevance sorts
            sorted = [...filtered];

            // First, separate in-stock and out-of-stock products
            const inStock = sorted.filter(p => p.status === 1);
            const outOfStock = sorted.filter(p => p.status !== 1);

            // Sort each group separately
            const sortProducts = (products: Types.ProductFull[]) => {
                switch (sortBy) {
                    case 'price-asc':
                        products.sort((a, b) => {
                            const priceA = a.price_promo || a.price_partner;
                            const priceB = b.price_promo || b.price_partner;
                            return priceA - priceB;
                        });
                        break;
                    case 'price-desc':
                        products.sort((a, b) => {
                            const priceA = a.price_promo || a.price_partner;
                            const priceB = b.price_promo || b.price_partner;
                            return priceB - priceA;
                        });
                        break;
                    case 'name':
                        products.sort((a, b) => {
                            const nameA = a.name.find(n => n.language_code === 'bg')?.text || '';
                            const nameB = b.name.find(n => n.language_code === 'bg')?.text || '';
                            return nameA.localeCompare(nameB);
                        });
                        break;
                    case 'relevance':
                        // Handled above - relevance keeps the backend's order
                        break;
                }
            };

            sortProducts(inStock);
            sortProducts(outOfStock);

            // Combine: in-stock items first, then out-of-stock items
            sorted = [...inStock, ...outOfStock];
        }

        return sorted;
    }, [allProducts, selectedManufacturers, priceRange, sortBy, query]);

    // Update filtered products and pagination
    useEffect(() => {
        const pageCount = Math.ceil(processedProducts.length / productsPerPage);
        setFilteredProducts(processedProducts);
        setTotalPages(pageCount);
        // Only clamp once there is something to page through - during the
        // initial load the count is 0 and would throw away a restored page.
        if (pageCount > 0 && currentPage > pageCount) {
            setCurrentPage(1);
        }
    }, [processedProducts, currentPage]);

    // Coming back from a product returns to the same page, filters and offset.
    useScrollRestoration({
        ready: !loading,
        state: {
            currentPage,
            sortBy,
            manufacturers: Array.from(selectedManufacturers),
            priceRange,
        },
        onRestore: (saved) => {
            setCurrentPage(saved.currentPage);
            setSortBy(saved.sortBy);
            setSelectedManufacturers(new Set(saved.manufacturers));
            setPriceRange(saved.priceRange);
        },
    });

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
        window.scrollTo(0, 0);
    };

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    const handleManufacturerToggle = (manufacturer: string) => {
        const newSet = new Set(selectedManufacturers);
        if (newSet.has(manufacturer)) {
            newSet.delete(manufacturer);
        } else {
            newSet.add(manufacturer);
        }
        setSelectedManufacturers(newSet);
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setSelectedManufacturers(new Set());
        setPriceRange({min: null, max: null});
        setSortBy('relevance');
        setCurrentPage(1);
    };

    const hasActiveFilters = selectedManufacturers.size > 0 || priceRange.min !== null || priceRange.max !== null;

    return (
        <Layout cartItemsCount={cart.items.length} cartTotal={cart.total}>
            <div className="container mx-auto py-6">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold mb-2">Резултати от търсенето</h1>
                    <div className="flex items-center justify-between">
                        <p className="text-gray-600">Показани са резултати за: "{query}"</p>
                        {hasActiveFilters && (
                            <button
                                onClick={clearFilters}
                                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                            >
                                <X className="h-4 w-4" />
                                Изчисти филтрите
                            </button>
                        )}
                    </div>
                </div>

                <div className="flex flex-col md:flex-row gap-6">
                    {/* Филтри */}
                    <div className={`md:w-1/4 ${showFilters ? 'block' : 'hidden md:block'}`}>
                        <div className="bg-white border border-gray-200 rounded-lg p-4 sticky top-4">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-bold">Филтри</h2>
                                {hasActiveFilters && (
                                    <span className="bg-primary-100 text-primary-700 text-xs px-2 py-1 rounded-full">
                                        {selectedManufacturers.size + (priceRange.min !== null || priceRange.max !== null ? 1 : 0)}
                                    </span>
                                )}
                            </div>

                            {/* Производител */}
                            {manufacturers.length > 0 && (
                                <div className="border-t border-gray-200 py-3">
                                    <h3 className="font-medium mb-2">Производител</h3>
                                    <div className="space-y-2 max-h-48 overflow-y-auto">
                                        {manufacturers.slice(0, 10).map(manufacturer => (
                                            <label key={manufacturer} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                                                <input 
                                                    type="checkbox" 
                                                    className="mr-2 text-primary-600 focus:ring-primary-500" 
                                                    checked={selectedManufacturers.has(manufacturer)}
                                                    onChange={() => handleManufacturerToggle(manufacturer)}
                                                />
                                                <span className="text-sm">{manufacturer}</span>
                                            </label>
                                        ))}
                                        {manufacturers.length > 10 && (
                                            <button className="text-xs text-primary-600 hover:text-primary-700">
                                                Покажи още ({manufacturers.length - 10})
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Цена */}
                            <div className="border-t border-gray-200 py-3">
                                <h3 className="font-medium mb-2">Цена (€)</h3>
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="number"
                                        placeholder="От"
                                        className="border border-gray-300 rounded px-2 py-1 w-full text-sm"
                                        value={priceRange.min || ''}
                                        onChange={(e) => {
                                            const value = e.target.value ? parseFloat(e.target.value) : null;
                                            setPriceRange(prev => ({...prev, min: value}));
                                            setCurrentPage(1);
                                        }}
                                    />
                                    <span className="text-gray-500">-</span>
                                    <input
                                        type="number"
                                        placeholder="До"
                                        className="border border-gray-300 rounded px-2 py-1 w-full text-sm"
                                        value={priceRange.max || ''}
                                        onChange={(e) => {
                                            const value = e.target.value ? parseFloat(e.target.value) : null;
                                            setPriceRange(prev => ({...prev, max: value}));
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                            </div>

                            {hasActiveFilters && (
                                <button 
                                    onClick={clearFilters}
                                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded mt-4 text-sm transition-colors"
                                >
                                    Изчисти всички филтри
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Продукти */}
                    <div className="flex-1">
                        <div className="flex justify-between items-center mb-6">
                            <p className="text-gray-600">
                                Намерени: <span className="font-medium">{filteredProducts.length}</span> продукта
                            </p>

                            <div className="flex items-center gap-2">
                                {/* Sort Dropdown */}
                                <div className="relative">
                                    <button
                                        onClick={() => setShowSortDropdown(!showSortDropdown)}
                                        className="flex items-center text-sm bg-white border border-gray-300 hover:bg-gray-50 px-3 py-2 rounded-lg transition-colors"
                                    >
                                        <SortAsc className="h-4 w-4 mr-2" />
                                        <span>
                                            {sortBy === 'relevance' && 'Подходящи'}
                                            {sortBy === 'price-asc' && 'Цена ↑'}
                                            {sortBy === 'price-desc' && 'Цена ↓'}
                                            {sortBy === 'name' && 'Име'}
                                        </span>
                                        <ChevronDown className="h-4 w-4 ml-1" />
                                    </button>
                                    
                                    {showSortDropdown && (
                                        <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                                            <button
                                                onClick={() => { setSortBy('relevance'); setShowSortDropdown(false); }}
                                                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${sortBy === 'relevance' ? 'bg-primary-50 text-primary-700' : ''}`}
                                            >
                                                Най-подходящи
                                            </button>
                                            <button
                                                onClick={() => { setSortBy('price-asc'); setShowSortDropdown(false); }}
                                                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${sortBy === 'price-asc' ? 'bg-primary-50 text-primary-700' : ''}`}
                                            >
                                                Цена: ниска към висока
                                            </button>
                                            <button
                                                onClick={() => { setSortBy('price-desc'); setShowSortDropdown(false); }}
                                                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${sortBy === 'price-desc' ? 'bg-primary-50 text-primary-700' : ''}`}
                                            >
                                                Цена: висока към ниска
                                            </button>
                                            <button
                                                onClick={() => { setSortBy('name'); setShowSortDropdown(false); }}
                                                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${sortBy === 'name' ? 'bg-primary-50 text-primary-700' : ''}`}
                                            >
                                                Име: А-Я
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={toggleFilters}
                                    className="md:hidden flex items-center text-sm bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg"
                                >
                                    <Filter className="h-4 w-4 mr-1" />
                                    Филтри
                                    {hasActiveFilters && (
                                        <span className="ml-1 bg-primary-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                                            {selectedManufacturers.size + (priceRange.min !== null || priceRange.max !== null ? 1 : 0)}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>

                        {loading ? (
                            <div className="flex justify-center py-8">
                                <p className="text-lg">Зареждане...</p>
                            </div>
                        ) : filteredProducts.length === 0 ? (
                            <div className="text-center py-8 bg-white border border-gray-200 rounded-lg">
                                <h2 className="text-xl font-medium mb-2">Няма намерени резултати</h2>
                                <p className="text-gray-600 mb-4">Опитайте с други ключови думи или разгледайте нашите категории.</p>
                                <Link to="/" className="text-blue-600 hover:underline">
                                    Върнете се към началната страница
                                </Link>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {filteredProducts
                                        .slice((currentPage - 1) * productsPerPage, currentPage * productsPerPage)
                                        .map((product) => (
                                            <ProductCard key={product.id} product={product} />
                                        ))}
                                </div>

                                {/* Пагинация */}
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                    maxVisiblePages={5}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default SearchResultsPage;